import React, { useState, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send, 
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import Reveal from './Reveal';

const sanitizeInputLive = (input: string): string => {
  if (!input) return '';

  return input
    .replace(/[<>]/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+=/gi, '')
    .replace(/&#/g, '');
};

const sanitizeInputSubmit = (input: string): string => {
  return sanitizeInputLive(input).trim();
};

const isWeb3FormsAccessKey = (key: string): boolean => {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(key);
};

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const FIELD_LIMITS = {
  name: 100,
  email: 255,
  company: 100,
  message: 2000,
  service: 50
};

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
    service: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const limit = FIELD_LIMITS[name as keyof typeof FIELD_LIMITS] || 1000;
    const truncatedValue = value.slice(0, limit);
    const sanitizedValue = sanitizeInputLive(truncatedValue);
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (recaptchaSiteKey && !recaptchaValue) {
      setErrors({ form: 'Por favor, completa el reCAPTCHA para continuar.' });
      return;
    }
    
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    } else if (formData.name.length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Por favor, ingresa un email válido';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'El mensaje es requerido';
    } else if (formData.message.length < 10) {
      newErrors.message = 'El mensaje debe tener al menos 10 caracteres';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    const sanitizedData = {
      name: sanitizeInputSubmit(formData.name),
      email: sanitizeInputSubmit(formData.email).toLowerCase(),
      company: sanitizeInputSubmit(formData.company),
      message: sanitizeInputSubmit(formData.message),
      service: sanitizeInputSubmit(formData.service)
    };
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      const web3formsKey = process.env.REACT_APP_WEB3FORMS_ACCESS_KEY;
      const contactEmail = process.env.REACT_APP_CONTACT_EMAIL || 'hello@hbeeit.com';
      const useWeb3Forms = Boolean(web3formsKey && isWeb3FormsAccessKey(web3formsKey));
      const serviceLabel = sanitizedData.service
        ? sanitizedData.service.replace(/-/g, ' ')
        : 'No especificado';

      let response: Response;
      let result: { success?: boolean | string; message?: string };

      if (useWeb3Forms) {
        response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            access_key: web3formsKey,
            name: sanitizedData.name,
            email: sanitizedData.email,
            message: sanitizedData.message,
            subject: `Contacto web - ${sanitizedData.name}`,
            company: sanitizedData.company || 'No especificada',
            service: serviceLabel,
            ...(recaptchaValue && { recaptcha_token: recaptchaValue }),
          }),
        });
        result = await response.json();
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Error al enviar el mensaje');
        }
      } else {
        response = await fetch(
          `https://formsubmit.co/ajax/${encodeURIComponent(contactEmail)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              name: sanitizedData.name,
              email: sanitizedData.email,
              message: sanitizedData.message,
              company: sanitizedData.company || 'No especificada',
              service: serviceLabel,
              _subject: `Contacto web - ${sanitizedData.name}`,
              _template: 'table',
              ...(recaptchaValue
                ? { 'g-recaptcha-response': recaptchaValue }
                : { _captcha: 'false' }),
            }),
          }
        );
        result = await response.json();
        if (!response.ok || result.success === false) {
          throw new Error(
            typeof result.message === 'string'
              ? result.message
              : 'Error al enviar el mensaje'
          );
        }
      }

      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        service: ''
      });
      
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    } catch (error) {
      setRecaptchaValue(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
      const detail =
        error instanceof Error && error.message
          ? error.message
          : 'Error desconocido';
      setErrors({
        form: `No pudimos enviar tu mensaje (${detail}). Intentá de nuevo o escribinos a hello@hbeeit.com`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaValue(value);
    if (value && errors.form) {
      setErrors(prev => {
        const next = { ...prev };
        delete next.form;
        return next;
      });
    }
  };

  const handleRecaptchaExpired = () => {
    setRecaptchaValue(null);
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Teléfono',
      details: ['(+54) 11 3189 1414'],
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email',
      details: ['hello@hbeeit.com'],
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Dirección',
      details: ['Centro Empresarial Libertador, Av. del Libertador 7208, C1429', 'Ciudad Autonoma de Buenos Aires', 'Argentina'],
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Horarios',
      details: ['Lunes - Viernes: 9:00 AM - 6:00 PM'],
    }
  ];

  return (
    <section id="contact" className="section-padding bg-secondary-900 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-20 w-64 h-64 bg-accent-500/8 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-accent-600/5 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <Reveal className="text-center mb-16">
          <div className="section-badge">
            <MessageSquare className="w-4 h-4 mr-2" />
            Contacto
          </div>

          <h2 className="section-title">
            Hablemos de tu
            <span className="text-accent-400 block">Proyecto</span>
          </h2>

          <p className="section-subtitle">
            ¿Listo para transformar tu infraestructura? Contáctanos y descubre cómo
            nuestras soluciones cloud pueden impulsar tu negocio.
          </p>
        </Reveal>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Información de Contacto</h3>
                <p className="text-gray-400 leading-relaxed">
                  Estamos aquí para ayudarte. Contáctanos a través de cualquiera de estos medios
                  y te responderemos lo antes posible.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                {contactInfo.map((info) => (
                  <div key={info.title} className="group">
                    <div className="surface-card p-6 h-full card-hover hover:border-accent-500/40">
                      <div className="text-accent-400 mb-4">
                        {info.icon}
                      </div>
                      <h4 className="text-lg font-bold text-white mb-3">{info.title}</h4>
                      <div className="space-y-1">
                        {info.details.map((detail) => (
                          <p key={detail} className="text-gray-400 text-sm leading-relaxed">{detail}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="surface-card p-8 shadow-xl shadow-black/30">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">Envíanos un Mensaje</h3>
                <p className="text-gray-400">Completa el formulario y nos pondremos en contacto contigo.</p>
              </div>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-accent-400 mx-auto mb-4" />
                  <h4 className="text-2xl font-bold text-white mb-2">¡Mensaje Enviado!</h4>
                  <p className="text-gray-400">Te contactaremos pronto.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Nombre *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        maxLength={FIELD_LIMITS.name}
                        className={`form-input ${
                          errors.name ? 'border-red-500 ring-1 ring-red-500' : ''
                        }`}
                        placeholder="Tu nombre completo"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-400">{errors.name}</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        maxLength={FIELD_LIMITS.email}
                        className={`form-input ${
                          errors.email ? 'border-red-500 ring-1 ring-red-500' : ''
                        }`}
                        placeholder="tu@email.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-400">{errors.email}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Empresa
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      maxLength={FIELD_LIMITS.company}
                      className="form-input"
                      placeholder="Nombre de tu empresa"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Servicio de Interés
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="">Selecciona un servicio</option>
                      <option value="cloud-assessment">Cloud Assessment</option>
                      <option value="cloud-onboarding">Cloud Onboarding</option>
                      <option value="devsecops">DevSecOps</option>
                      <option value="devops">DevOps</option>
                      <option value="finops">FinOps</option>
                      <option value="consultoria">Consultoría Técnica</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Mensaje *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      maxLength={FIELD_LIMITS.message}
                      className={`form-input resize-none ${
                        errors.message ? 'border-red-500 ring-1 ring-red-500' : ''
                      }`}
                      placeholder="Cuéntanos sobre tu proyecto..."
                    />
                    <div className="mt-1 flex justify-between items-center">
                      {errors.message ? (
                        <p className="text-sm text-red-400">{errors.message}</p>
                      ) : (
                        <span></span>
                      )}
                      <p className="text-xs text-gray-500">
                        {formData.message.length}/{FIELD_LIMITS.message}
                      </p>
                    </div>
                  </div>

                  {errors.form && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                      <p className="text-sm text-red-400">{errors.form}</p>
                    </div>
                  )}
                  
                  {recaptchaSiteKey ? (
                    <div className="flex flex-col items-center gap-2">
                      <ReCAPTCHA
                        ref={recaptchaRef}
                        sitekey={recaptchaSiteKey}
                        onChange={handleRecaptchaChange}
                        onExpired={handleRecaptchaExpired}
                        theme="dark"
                      />
                      <p className="text-xs text-gray-500">
                        Verificación reCAPTCHA requerida para enviar el formulario
                      </p>
                    </div>
                  ) : (
                    <div className="bg-accent-500/10 border border-accent-500/30 rounded-xl p-4">
                      <p className="text-sm text-accent-300">
                        reCAPTCHA no está configurado. Agregá REACT_APP_RECAPTCHA_SITE_KEY al build de producción.
                      </p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting || !!(recaptchaSiteKey && !recaptchaValue)}
                    className="w-full btn-primary py-4 px-8 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Enviando...
                      </span>
                    ) : (
                      <span className="flex items-center justify-center">
                        <Send className="w-5 h-5 mr-2" />
                        Enviar Mensaje
                      </span>
                    )}
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
